"use client"

import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"
import { font_title_secularOne } from "../fonts"

/** the main select warper */
const Select = SelectPrimitive.Root

/** a group of options:
 * - first child: Label
 * - (n) th child: Item
 */
const SelectGroup = SelectPrimitive.Group

/** select's value display - used also for place holder */
const SelectValue = SelectPrimitive.Value

/** select click trigger - value should be inside*/
const SelectTrigger = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    dir="rtl"
    ref={ ref }
    className={ cn(
      "flex hover:bg-nfPurple-950 transition-colors min-h-fit h-10 gap-2 w-full items-center justify-between rounded-md border  px-3 py-0 text-md  focus:outline-none focus:ring-2  focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 border-nfPurple-600 bg-slate-950 ring-offset-slate-950 placeholder:text-slate-400 focus:ring-nfBlue-200",
      className
    ) }
    { ...props }
  >
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
    { children } <span />

  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ ref }
    className={ cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ) }
    { ...props }
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ ref }
    className={ cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ) }
    { ...props }
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

/** all the items / labels / groups should be inside */
const SelectContent = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ (ref) => {
        // temporary workaround from https://github.com/shadcn-ui/ui/issues/1220
        //ref?.addEventListener('touchend', (e) => e.preventDefault())
      }
      }
      dir="rtl"
      onClick={ (e) => e.stopPropagation() }
      className={ cn(
        " relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border shadow-black/50 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 border-nfPurple-600 bg-black/50 backdrop-blur-md text-white",
        position === "popper" &&
        "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      ) }
      position={ position }
      { ...props }
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={ cn(
          "p-1",
          position === "popper" &&
          "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        ) }
      >
        { children }
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

/** non selectable text item */
const SelectLabel = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ ref }
    className={ cn("text-center py-1.5 pl-8 pr-2 text-2xl font-semibold", font_title_secularOne.className, className) }
    { ...props }
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

/** selectable item */
const SelectItem = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ ref }
    className={ cn(
      "relative cursor-pointer flex w-full select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-md outline-none  data-[disabled]:pointer-events-none data-[disabled]:opacity-50 focus:bg-nfPurple-900 focus:text-slate-50 data-[state='checked']:text-lime-300",
      className
    ) }
    { ...props }
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center ">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText className="text-center">{ children }</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ ref }
    className={ cn("-mx-1 my-1 h-px ", className) }
    { ...props }
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue
}

